/**
 * admin/AdminBookings.js — Bookings (the "PMS" view for pathways)
 *
 *   Overview  occupancy at a glance: pathway timeline, rooms, activities, kitchen list
 *   Bookings  who booked what — search, filter, edit anything, cancel, export CSV
 *   Setup     what this retreat sells: pathways, activities offered, room upcharges
 *   Catalog   the shared lists every retreat picks from: activities, diet options
 *
 * Everything is adjustable: staff can move someone to a full pathway (with
 * "allow over capacity"), override a price, or change a diet on the day.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  HiOutlineTicket, HiOutlinePlus, HiOutlinePencilSquare, HiOutlineTrash, HiOutlineXMark,
  HiOutlineArrowDownTray, HiOutlineMagnifyingGlass, HiOutlineCalendarDays, HiOutlineSparkles,
  HiOutlineHome, HiOutlineSun, HiOutlineHeart, HiOutlineUsers, HiOutlineCurrencyDollar,
  HiOutlineDocumentDuplicate, HiOutlineExclamationTriangle, HiOutlineCheckCircle, HiOutlineCog6Tooth,
  HiOutlineSquares2X2, HiOutlineQueueList,
} from "react-icons/hi2";
import toast from "react-hot-toast";
import ImagePicker from "./ImagePicker";
import AdminLayout from "./AdminLayout";
import { bookingsAPI, retreatBlocksAPI, roomTypesAPI } from "../services/api";
import { useCurrentAdmin, hasPermission } from "./permissions";
import "./AdminBookings.css";

/* ── helpers ── */
const fmt = (d, o = { month: "short", day: "numeric" }) => (d ? new Date(d).toLocaleDateString("en-US", { ...o, timeZone: "UTC" }) : "—");
const fmtY = (d) => fmt(d, { month: "short", day: "numeric", year: "numeric" });
const iso = (d) => (d ? String(d).slice(0, 10) : "");
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const pct = (a, b) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0);
const nightsBetween = (a, b) => Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000));

function Bar({ value, max, tone }) {
  const p = pct(value, max);
  const over = value > max;
  return (
    <div className="abk__bar" role="img" aria-label={`${value} of ${max}`}>
      <span className={`abk__bar-fill ${over ? "is-over" : p >= 100 ? "is-full" : tone ? `is-${tone}` : ""}`} style={{ width: `${over ? 100 : p}%` }} />
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="abk__overlay" onClick={onClose}>
      <div className={`abk__modal ${wide ? "abk__modal--wide" : ""}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className="abk__modal-head">
          <h3>{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close"><HiOutlineXMark /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   PATHWAY MODAL
════════════════════════════════════════════════════════ */
function PathwayModal({ block, pathway, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    name: pathway?.name || "",
    description: pathway?.description || "",
    highlights: (pathway?.highlights || []).join("\n"),
    start_date: iso(pathway?.start_date) || iso(block.start_date),
    end_date: iso(pathway?.end_date) || iso(block.end_date),
    capacity: pathway?.capacity ?? 12,
    price: pathway?.price ?? 0,
    sort_order: pathway?.sort_order ?? 0,
    active: pathway ? !!pathway.active : true,
    image_path: pathway?.image_path || "",
    slug: pathway?.slug || "",
    tagline: pathway?.tagline || "",
    focus: pathway?.focus || "",
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => { setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value })); setError(""); };
  const nights = f.start_date && f.end_date ? nightsBetween(f.start_date, f.end_date) : 0;

  const save = async () => {
    if (!f.name.trim()) { setError("Give the pathway a name."); return; }
    setSaving(true);
    try {
      const payload = { ...f, capacity: parseInt(f.capacity, 10), price: Number(f.price), sort_order: parseInt(f.sort_order, 10) || 0 };
      const res = pathway ? await bookingsAPI.updatePathway(pathway.id, payload) : await bookingsAPI.createPathway(block.id, payload);
      toast.success(res?.message || "Saved.");
      onSaved();
    } catch (err) { setError(err.message || "Could not save."); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={pathway ? `Edit ${pathway.name}` : "New pathway"} onClose={onClose} wide>
      <div className="abk__form">
        <label className="abk__field abk__field--full">
          <span>Name</span>
          <input className="abk__input" value={f.name} onChange={set("name")} placeholder="e.g. Ayahuasca" autoFocus />
        </label>
        <label className="abk__field">
          <span>Starts</span>
          <input type="date" className="abk__input" min={iso(block.start_date)} max={iso(block.end_date)} value={f.start_date} onChange={set("start_date")} />
        </label>
        <label className="abk__field">
          <span>Ends</span>
          <input type="date" className="abk__input" min={f.start_date || iso(block.start_date)} max={iso(block.end_date)} value={f.end_date} onChange={set("end_date")} />
        </label>
        <label className="abk__field">
          <span>Places</span>
          <input type="number" min="0" className="abk__input" value={f.capacity} onChange={set("capacity")} />
        </label>
        <label className="abk__field">
          <span>Price per person (USD)</span>
          <input type="number" min="0" step="50" className="abk__input" value={f.price} onChange={set("price")} />
        </label>
        <label className="abk__field abk__field--full">
          <span>Short description <em>(shown to members)</em></span>
          <textarea className="abk__input" rows={2} value={f.description} onChange={set("description")} />
        </label>
        <label className="abk__field abk__field--full">
          <span>Highlights <em>(one per line — shown as a checklist)</em></span>
          <textarea className="abk__input" rows={3} value={f.highlights} onChange={set("highlights")} placeholder={"Two ceremonies\nDaily integration circle"} />
        </label>
        <label className="abk__field">
          <span>Display order</span>
          <input type="number" className="abk__input" value={f.sort_order} onChange={set("sort_order")} />
        </label>

        {/* What the public website shows for this retreat. */}
        <label className="abk__field">
          <span>Badge <em>(optional, e.g. "Last few places")</em></span>
          <input className="abk__input" value={f.tagline} onChange={set("tagline")} maxLength={200} />
        </label>
        <label className="abk__field abk__field--full">
          <span>Focus tags <em>(comma separated, e.g. Trauma healing, Emotional release)</em></span>
          <input className="abk__input" value={f.focus} onChange={set("focus")} maxLength={255} />
        </label>
        <label className="abk__field abk__field--full">
          <span>Web address <em>(leave blank to build it from the name)</em></span>
          <input className="abk__input" value={f.slug} onChange={set("slug")} placeholder="ayahuasca-retreat" />
        </label>

        <div className="abk__field--full">
          <ImagePicker
            label="Picture on the website"
            value={f.image_path}
            onChange={(v) => { setF((x) => ({ ...x, image_path: v })); setError(""); }}
          />
        </div>
        <label className="abk__check">
          <input type="checkbox" checked={f.active} onChange={set("active")} /> Open for booking
        </label>
      </div>
      <p className="abk__hint"><HiOutlineCalendarDays /> {nights} night{nights !== 1 ? "s" : ""} · room upcharges are multiplied by this.{pathway && Number(pathway.taken) > 0 ? " Price changes don't affect people already booked." : ""}</p>
      {error && <p className="abk__error">{error}</p>}
      <div className="abk__modal-actions">
        <button type="button" className="a-btn a-btn--outline" onClick={onClose}>Cancel</button>
        <button type="button" className="a-btn a-btn--rust" onClick={save} disabled={saving}>{saving ? "Saving…" : pathway ? "Save changes" : "Add pathway"}</button>
      </div>
    </Modal>
  );
}

/* ════════════════════════════════════════════════════════
   BOOKING EDIT MODAL
════════════════════════════════════════════════════════ */
function BookingModal({ booking, data, dietsCatalog, canManage, onClose, onSaved }) {
  const [f, setF] = useState({
    pathway_id: booking.pathway_id || "",
    room_type_id: booking.room_type_id || "",
    block_activity_id: booking.block_activity_id || "",
    diet_option_id: booking.diet_option_id || "",
    diet_notes: booking.diet_notes || "",
    admin_notes: booking.admin_notes || "",
    total_override: "",
    force: false,
  });
  const [cancelReason, setCancelReason] = useState("");
  const [returnToQueue, setReturnToQueue] = useState(true);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => { setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value })); setError(""); };
  const readOnly = !canManage || booking.status !== "confirmed";

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        pathway_id: f.pathway_id || null, room_type_id: f.room_type_id || null,
        block_activity_id: f.block_activity_id || null, diet_option_id: f.diet_option_id || null,
        diet_notes: f.diet_notes, admin_notes: f.admin_notes, force: f.force,
      };
      if (f.total_override !== "") payload.total_override = f.total_override;
      const res = await bookingsAPI.update(booking.id, payload);
      toast.success(res?.message || "Booking updated.");
      onSaved();
    } catch (err) { setError(err.message || "Could not update."); }
    finally { setSaving(false); }
  };

  const cancel = async () => {
    setSaving(true);
    try {
      const res = await bookingsAPI.cancel(booking.id, { reason: cancelReason, return_to_queue: returnToQueue });
      toast.success(res?.message || "Booking cancelled.");
      onSaved();
    } catch (err) { setError(err.message || "Could not cancel."); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`${booking.first_name} ${booking.last_name || ""}`} onClose={onClose} wide>
      <p className="abk__modal-sub">
        {booking.email}{booking.phone ? ` · ${booking.phone}` : ""} · booked {fmtY(booking.created_at)}{booking.source === "admin" ? " by staff" : ""}
        {booking.application_id && <> · <Link to={`/admin/applications?id=${booking.application_id}`}>Open application</Link></>}
      </p>
      {booking.status !== "confirmed" && (
        <p className={`abk__status-note is-${booking.status}`}>
          {booking.status === "cancelled" ? `Cancelled ${fmtY(booking.cancelled_at)}${booking.cancel_reason ? ` — ${booking.cancel_reason}` : ""}` : "This retreat is completed — booking kept for records."}
        </p>
      )}
      <div className="abk__form">
        {data.pathways.length > 0 && (
          <label className="abk__field">
            <span>Pathway</span>
            <select className="abk__input" value={f.pathway_id} onChange={set("pathway_id")} disabled={readOnly}>
              {data.pathways.map((p) => (
                <option key={p.id} value={p.id}>{p.name} · {fmt(p.start_date)}–{fmt(p.end_date)} · {p.remaining} left{!p.active ? " (hidden)" : ""}</option>
              ))}
            </select>
          </label>
        )}
        {data.roomTypes.length > 0 && (
          <label className="abk__field">
            <span>Room</span>
            <select className="abk__input" value={f.room_type_id} onChange={set("room_type_id")} disabled={readOnly}>
              {data.roomTypes.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.remaining} left</option>)}
            </select>
          </label>
        )}
        <label className="abk__field">
          <span>Activity</span>
          <select className="abk__input" value={f.block_activity_id} onChange={set("block_activity_id")} disabled={readOnly}>
            <option value="">Not chosen</option>
            {data.activities.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.remaining} left</option>)}
          </select>
        </label>
        <label className="abk__field">
          <span>Diet</span>
          <select className="abk__input" value={f.diet_option_id} onChange={set("diet_option_id")} disabled={readOnly}>
            <option value="">Not specified</option>
            {dietsCatalog.map((d) => <option key={d.id} value={d.id}>{d.name}{!d.active ? " (hidden)" : ""}</option>)}
          </select>
        </label>
        <label className="abk__field abk__field--full">
          <span>Diet notes / allergies</span>
          <input className="abk__input" value={f.diet_notes} onChange={set("diet_notes")} disabled={readOnly} />
        </label>
        <label className="abk__field">
          <span>Total <em>(now {money(booking.total)})</em></span>
          <input type="number" min="0" className="abk__input" placeholder="Leave blank to keep / recalculate" value={f.total_override} onChange={set("total_override")} disabled={readOnly} />
        </label>
        <label className="abk__field">
          <span>Internal notes <em>(staff only)</em></span>
          <input className="abk__input" value={f.admin_notes} onChange={set("admin_notes")} disabled={readOnly} />
        </label>
        {!readOnly && (
          <label className="abk__check abk__field--full">
            <input type="checkbox" checked={f.force} onChange={set("force")} /> Allow over capacity (emergencies, staff decisions)
          </label>
        )}
      </div>
      <p className="abk__hint">Changing the pathway or room recalculates the total unless you set one. Paid so far: {money(booking.paid)}.</p>
      {error && <p className="abk__error">{error}</p>}

      {!readOnly && (
        <>
          <div className="abk__modal-actions">
            <button type="button" className="a-btn a-btn--ghost abk__danger-link" onClick={() => setConfirmCancel((v) => !v)}>
              <HiOutlineTrash /> Cancel booking…
            </button>
            <span className="abk__spacer" />
            <button type="button" className="a-btn a-btn--outline" onClick={onClose}>Close</button>
            <button type="button" className="a-btn a-btn--rust" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
          </div>
          {confirmCancel && (
            <div className="abk__cancel-box">
              <p><HiOutlineExclamationTriangle /> Cancelling frees their pathway, room, activity and bed.</p>
              <input className="abk__input" placeholder="Reason (optional, internal)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
              <label className="abk__check"><input type="checkbox" checked={returnToQueue} onChange={(e) => setReturnToQueue(e.target.checked)} /> Put them back in the queue at their original place</label>
              <button type="button" className="a-btn abk__btn-danger" onClick={cancel} disabled={saving}>Confirm cancellation</button>
            </div>
          )}
        </>
      )}
      {readOnly && (
        <div className="abk__modal-actions"><button type="button" className="a-btn a-btn--outline" onClick={onClose}>Close</button></div>
      )}
    </Modal>
  );
}

/* ════════════════════════════════════════════════════════
   OVERVIEW TAB
════════════════════════════════════════════════════════ */
function Overview({ data }) {
  const { block, pathways, roomTypes, activities, totals, diets, dietNotes, legacy } = data;
  const nights = block.nights || nightsBetween(block.start_date, block.end_date);
  const days = Array.from({ length: nights + 1 }, (_, i) => {
    const d = new Date(block.start_date); d.setUTCDate(d.getUTCDate() + i); return d;
  });
  const capacity = totals.pathwayCapacity || totals.roomCapacity;

  return (
    <div className="abk__overview">
      <div className="abk__kpis">
        <div className="acard abk__kpi"><HiOutlineUsers /><div><strong>{totals.booked}{capacity ? <small> / {capacity}</small> : null}</strong><span>Booked</span></div></div>
        <div className="acard abk__kpi"><HiOutlineCurrencyDollar /><div><strong>{money(totals.revenue)}</strong><span>Booked value</span></div></div>
        <div className="acard abk__kpi"><HiOutlineCheckCircle /><div><strong>{money(totals.paid)}</strong><span>Paid so far</span></div></div>
        <div className="acard abk__kpi"><HiOutlineXMark /><div><strong>{totals.cancelled}</strong><span>Cancelled</span></div></div>
      </div>

      {legacy.length > 0 && (
        <p className="abk__legacy-note"><HiOutlineExclamationTriangle /> {legacy.length} {legacy.length === 1 ? "person was" : "people were"} confirmed before pathways were set up. They're listed on the Bookings tab.</p>
      )}

      {pathways.length > 0 ? (
        <div className="acard abk__timeline">
          <p className="abk__card-title"><HiOutlineCalendarDays /> Pathway timeline</p>
          <div className="abk__tl" tabIndex={0} role="region" aria-label="Pathway timeline" style={{ "--cols": days.length }}>
            <div className="abk__tl-days">
              {days.map((d) => <span key={d.toISOString()}>{fmt(d, { weekday: "short" })}<b>{fmt(d, { day: "numeric" })}</b></span>)}
            </div>
            {pathways.map((p) => {
              const start = nightsBetween(block.start_date, p.start_date);
              const len = Math.max(1, nightsBetween(p.start_date, p.end_date) + 1);
              return (
                <div key={p.id} className={`abk__tl-row ${!p.active ? "is-hidden" : ""}`}>
                  <div className="abk__tl-bar" style={{ gridColumn: `${start + 1} / span ${len}` }} title={`${p.name}: ${p.taken} of ${p.capacity} booked`}>
                    <span className="abk__tl-fill" style={{ width: `${pct(p.taken, p.capacity)}%` }} />
                    <span className="abk__tl-label"><b>{p.name}</b> {p.taken}/{p.capacity}{!p.active ? " · hidden" : ""}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="acard abk__empty-inline">
          <HiOutlineSparkles /> <span>No pathways yet — members will book this retreat by room only. Add pathways on the <b>Setup</b> tab.</span>
        </div>
      )}

      <div className="abk__occ-grid">
        {pathways.length > 0 && (
          <div className="acard">
            <p className="abk__card-title"><HiOutlineSparkles /> Pathways</p>
            {pathways.map((p) => (
              <div key={p.id} className="abk__occ">
                <div className="abk__occ-top"><span>{p.name}</span><b>{p.taken}/{p.capacity}</b></div>
                <Bar value={p.taken} max={p.capacity} />
                <small>{fmt(p.start_date)}–{fmt(p.end_date)} · {money(p.price)}</small>
              </div>
            ))}
          </div>
        )}
        <div className="acard">
          <p className="abk__card-title"><HiOutlineHome /> Rooms</p>
          {roomTypes.length === 0 && <p className="abk__muted">No room types allocated to this retreat. Set them in Retreat Blocks.</p>}
          {roomTypes.map((r) => (
            <div key={r.id} className="abk__occ">
              <div className="abk__occ-top"><span>{r.name}</span><b>{r.taken}/{r.capacity}</b></div>
              <Bar value={r.taken} max={r.capacity} tone="forest" />
              <small>{r.is_free ? "Included" : Number(r.nightly_rate) > 0 ? `+${money(r.nightly_rate)}/night` : "No upcharge"}</small>
            </div>
          ))}
        </div>
        <div className="acard">
          <p className="abk__card-title"><HiOutlineSun /> Activities</p>
          {activities.length === 0 && <p className="abk__muted">No activities offered at this retreat yet.</p>}
          {activities.map((a) => (
            <div key={a.id} className="abk__occ">
              <div className="abk__occ-top"><span>{a.name}</span><b>{a.taken}/{a.capacity}</b></div>
              <Bar value={a.taken} max={a.capacity} tone="amber" />
            </div>
          ))}
        </div>
        <div className="acard">
          <p className="abk__card-title"><HiOutlineHeart /> Kitchen list</p>
          {diets.length === 0 && <p className="abk__muted">No bookings yet.</p>}
          <ul className="abk__diets">
            {diets.map((d) => <li key={d.name}><span>{d.name}</span><b>{d.count}</b></li>)}
          </ul>
          {dietNotes.length > 0 && (
            <div className="abk__diet-notes">
              <p>Allergies &amp; notes</p>
              {dietNotes.map((n, i) => <div key={i}><b>{n.name}:</b> {n.notes}</div>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   BOOKINGS TAB
════════════════════════════════════════════════════════ */
function BookingsTable({ data, onOpen }) {
  const [q, setQ] = useState("");
  const [pathway, setPathway] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return data.bookings.filter((b) => {
      if (!showCancelled && b.status === "cancelled") return false;
      if (pathway && String(b.pathway_id) !== pathway) return false;
      if (!term) return true;
      return [b.first_name, b.last_name, b.email, b.pathway_name, b.room_type_name, b.activity_name, b.diet_name]
        .filter(Boolean).some((v) => v.toLowerCase().includes(term));
    });
  }, [data.bookings, q, pathway, showCancelled]);

  return (
    <div className="acard abk__table-card">
      <div className="abk__filters">
        <label className="abk__search">
          <HiOutlineMagnifyingGlass />
          <input placeholder="Search name, email, room, activity…" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {data.pathways.length > 0 && (
          <select className="abk__input abk__input--sm" value={pathway} onChange={(e) => setPathway(e.target.value)}>
            <option value="">All pathways</option>
            {data.pathways.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <label className="abk__check"><input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} /> Show cancelled</label>
        <span className="abk__count">{rows.length} booking{rows.length !== 1 ? "s" : ""}</span>
      </div>

      {rows.length === 0 && data.legacy.length === 0 ? (
        <div className="abk__empty"><HiOutlineTicket /><p>No bookings {q || pathway ? "match your filters" : "yet"}.</p></div>
      ) : (
        <div className="abk__table-wrap">
          <table className="abk__table">
            <thead>
              <tr><th>Guest</th><th>Pathway</th><th>Room</th><th>Activity</th><th>Diet</th><th className="num">Total</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className={b.status !== "confirmed" ? `is-${b.status}` : ""} onClick={() => onOpen(b)}>
                  <td data-label="Guest">
                    <div className="abk__guest">
                      <span className="abk__avatar">{(b.first_name || "?")[0]}{(b.last_name || "")[0]}</span>
                      <span><b>{b.first_name} {b.last_name}</b><small>{b.category || b.email}</small></span>
                    </div>
                  </td>
                  <td data-label="Pathway">{b.pathway_name ? <><b>{b.pathway_name}</b><small>{fmt(b.pathway_start_date)}–{fmt(b.pathway_end_date)}</small></> : <span className="abk__muted">—</span>}</td>
                  <td data-label="Room">{b.room_type_name || <span className="abk__muted">—</span>}</td>
                  <td data-label="Activity">{b.activity_name || <span className="abk__muted">Not chosen</span>}</td>
                  <td data-label="Diet">{b.diet_name || <span className="abk__muted">—</span>}{b.diet_notes && <small className="abk__flag">{b.diet_notes}</small>}</td>
                  <td data-label="Total" className="num">
                    <b>{money(b.total)}</b>
                    <small>{Number(b.paid) >= Number(b.total) && Number(b.total) > 0 ? "Paid" : Number(b.paid) > 0 ? `${money(b.paid)} paid` : b.status === "cancelled" ? "Cancelled" : ""}</small>
                  </td>
                  <td className="abk__row-action"><HiOutlinePencilSquare /></td>
                </tr>
              ))}
              {!pathway && !q && data.legacy.map((l) => (
                <tr key={`legacy-${l.person_id}`} className="is-legacy">
                  <td data-label="Guest"><div className="abk__guest"><span className="abk__avatar">{(l.first_name || "?")[0]}{(l.last_name || "")[0]}</span><span><b>{l.first_name} {l.last_name}</b><small>{l.email}</small></span></div></td>
                  <td data-label="Pathway" colSpan={4}><span className="abk__legacy-tag">Confirmed before pathways</span> {l.room_type_name ? `· ${l.room_type_name}` : ""}</td>
                  <td data-label="Total" className="num"><b>{money(l.total_owed)}</b></td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   SETUP TAB
════════════════════════════════════════════════════════ */
function Setup({ data, blocks, canEdit, onChanged }) {
  const { block, pathways, roomTypes } = data;
  const [editing, setEditing] = useState(null); // pathway or "new"
  const [copyFrom, setCopyFrom] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [offered, setOffered] = useState({}); // activity_id → { on, capacity, date }
  const [savingActs, setSavingActs] = useState(false);
  const [rates, setRates] = useState({});

  useEffect(() => {
    bookingsAPI.catalog.activities().then((r) => setCatalog(r?.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const m = {};
    data.activities.forEach((a) => { m[a.activity_id] = { on: true, capacity: a.capacity, date: iso(a.activity_date) }; });
    setOffered(m);
    const r = {};
    roomTypes.forEach((rt) => { r[rt.id] = rt.nightly_rate; });
    setRates(r);
  }, [data, roomTypes]);

  const removePathway = async (p) => {
    if (!window.confirm(`Remove "${p.name}"? ${Number(p.taken) > 0 ? "It has bookings, so it will be hidden instead." : ""}`)) return;
    try { const r = await bookingsAPI.removePathway(p.id); toast.success(r?.message || "Removed."); onChanged(); }
    catch (err) { toast.error(err.message); }
  };
  const copy = async () => {
    if (!copyFrom) return;
    try { const r = await bookingsAPI.copyPathways(block.id, copyFrom); toast.success(r?.message || "Copied."); setCopyFrom(""); onChanged(); }
    catch (err) { toast.error(err.message); }
  };
  const saveActivities = async () => {
    setSavingActs(true);
    try {
      const list = Object.entries(offered).filter(([, v]) => v.on).map(([id, v]) => ({ activity_id: Number(id), capacity: parseInt(v.capacity, 10) || 0, activity_date: v.date || null }));
      const r = await bookingsAPI.setActivities(block.id, list);
      toast.success(r?.message || "Saved.");
      onChanged();
    } catch (err) { toast.error(err.message); }
    finally { setSavingActs(false); }
  };
  const saveRate = async (rt) => {
    const v = rates[rt.id];
    if (Number(v) === Number(rt.nightly_rate)) return;
    try { await roomTypesAPI.update(rt.id, { nightly_rate: Number(v) || 0 }); toast.success(`${rt.name}: upcharge saved.`); onChanged(); }
    catch (err) { toast.error(err.message); }
  };
  const others = blocks.filter((b) => b.id !== block.id);

  return (
    <div className="abk__setup">
      {/* Pathways */}
      <div className="acard">
        <div className="abk__section-head">
          <div>
            <p className="abk__card-title"><HiOutlineSparkles /> Pathways</p>
            <p className="abk__muted">Bookable programmes inside this retreat, each with its own dates, places and price.</p>
          </div>
          {canEdit && (
            <div className="abk__head-actions">
              {others.length > 0 && (
                <div className="abk__copy">
                  <select className="abk__input abk__input--sm" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
                    <option value="">Copy from another retreat…</option>
                    {others.map((b) => <option key={b.id} value={b.id}>{b.name} ({fmtY(b.start_date)})</option>)}
                  </select>
                  <button type="button" className="a-btn a-btn--outline" disabled={!copyFrom} onClick={copy}><HiOutlineDocumentDuplicate /> Copy</button>
                </div>
              )}
              <button type="button" className="a-btn a-btn--rust" onClick={() => setEditing("new")}><HiOutlinePlus /> Add pathway</button>
            </div>
          )}
        </div>
        {pathways.length === 0 ? (
          <div className="abk__empty"><HiOutlineSparkles /><p>No pathways yet. Without pathways, members book this retreat by room only.</p></div>
        ) : (
          <div className="abk__pw-grid">
            {pathways.map((p) => (
              <div key={p.id} className={`abk__pw ${!p.active ? "is-hidden" : ""}`}>
                <div className="abk__pw-top">
                  <b>{p.name}</b>
                  {!p.active && <span className="abk__chip">Hidden</span>}
                </div>
                <p className="abk__pw-dates"><HiOutlineCalendarDays /> {fmt(p.start_date)} – {fmtY(p.end_date)} · {p.nights} nights</p>
                <div className="abk__pw-stats">
                  <span><small>Price</small>{money(p.price)}</span>
                  <span><small>Booked</small>{p.taken}/{p.capacity}</span>
                </div>
                <Bar value={p.taken} max={p.capacity} />
                {canEdit && (
                  <div className="abk__pw-actions">
                    <button type="button" className="a-btn a-btn--ghost" onClick={() => setEditing(p)}><HiOutlinePencilSquare /> Edit</button>
                    <button type="button" className="a-btn a-btn--ghost abk__danger-link" onClick={() => removePathway(p)}><HiOutlineTrash /> Remove</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Activities offered here */}
      <div className="acard">
        <div className="abk__section-head">
          <div>
            <p className="abk__card-title"><HiOutlineSun /> Activities offered at this retreat</p>
            <p className="abk__muted">Each member picks one included activity. Tick the ones on offer and set how many places each has.</p>
          </div>
          {canEdit && <button type="button" className="a-btn a-btn--rust" onClick={saveActivities} disabled={savingActs}>{savingActs ? "Saving…" : "Save activities"}</button>}
        </div>
        {catalog.length === 0 ? (
          <p className="abk__muted">No activities in the catalog yet — add some on the Catalog tab.</p>
        ) : (
          <div className="abk__act-list">
            {catalog.filter((a) => a.active || offered[a.id]?.on).map((a) => {
              const v = offered[a.id] || { on: false, capacity: a.default_capacity, date: "" };
              const booked = data.activities.find((x) => x.activity_id === a.id)?.taken || 0;
              return (
                <div key={a.id} className={`abk__act ${v.on ? "is-on" : ""}`}>
                  <label className="abk__check">
                    <input type="checkbox" disabled={!canEdit} checked={!!v.on} onChange={(e) => setOffered((o) => ({ ...o, [a.id]: { ...v, on: e.target.checked } }))} />
                    <span><b>{a.name}</b>{booked > 0 && <small>{booked} booked</small>}</span>
                  </label>
                  <label className="abk__inline">Places
                    <input type="number" min="0" className="abk__input abk__input--xs" disabled={!canEdit || !v.on} value={v.capacity} onChange={(e) => setOffered((o) => ({ ...o, [a.id]: { ...v, capacity: e.target.value } }))} />
                  </label>
                  <label className="abk__inline">Date <em>(optional)</em>
                    <input type="date" className="abk__input abk__input--sm" disabled={!canEdit || !v.on} min={iso(block.start_date)} max={iso(block.end_date)} value={v.date || ""} onChange={(e) => setOffered((o) => ({ ...o, [a.id]: { ...v, date: e.target.value } }))} />
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Room upcharges */}
      <div className="acard">
        <p className="abk__card-title"><HiOutlineHome /> Room upcharges</p>
        <p className="abk__muted">Per-night upgrade price, multiplied by the pathway's nights (e.g. $100 × 6 nights = $600). Upcharges apply to every retreat; how many of each room this retreat has is set in <Link to="/admin/retreat-blocks">Retreat Blocks</Link>.</p>
        {roomTypes.length === 0 ? (
          <p className="abk__muted">No room types allocated to this retreat yet.</p>
        ) : (
          <div className="abk__rates">
            {roomTypes.map((rt) => (
              <div key={rt.id} className="abk__rate">
                <span className="abk__rate-name"><b>{rt.name}</b><small>{rt.capacity} places here · {rt.taken} taken</small></span>
                {rt.is_free ? <span className="abk__chip abk__chip--green">Included</span> : (
                  <label className="abk__money">
                    <span>$</span>
                    <input type="number" min="0" step="10" className="abk__input abk__input--xs" disabled={!canEdit}
                      value={rates[rt.id] ?? ""} onChange={(e) => setRates((r) => ({ ...r, [rt.id]: e.target.value }))} onBlur={() => saveRate(rt)} />
                    <span>/ night</span>
                  </label>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <PathwayModal block={block} pathway={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   CATALOG TAB
════════════════════════════════════════════════════════ */
function CatalogList({ title, Icon, hint, items, fields, onSave, onRemove, canEdit, render }) {
  const blank = Object.fromEntries(fields.map((f) => [f.k, f.def ?? ""]));
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSave(form, editId); setForm(blank); setEditId(null); }
    finally { setBusy(false); }
  };

  return (
    <div className="acard">
      <p className="abk__card-title"><Icon /> {title}</p>
      <p className="abk__muted">{hint}</p>
      <ul className="abk__cat-list">
        {items.map((it) => (
          <li key={it.id} className={!it.active ? "is-hidden" : ""}>
            <span className="abk__cat-main">{render(it)}</span>
            {!it.active && <span className="abk__chip">Hidden</span>}
            {canEdit && (
              <span className="abk__cat-actions">
                <button type="button" className="a-btn a-btn--ghost" onClick={() => { setEditId(it.id); setForm(Object.fromEntries(fields.map((f) => [f.k, f.type === "checkbox" ? !!it[f.k] : it[f.k] ?? ""]))); }}><HiOutlinePencilSquare /></button>
                <button type="button" className="a-btn a-btn--ghost abk__danger-link" onClick={() => onRemove(it)}><HiOutlineTrash /></button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <form className="abk__cat-form" onSubmit={submit}>
          {fields.map((f) => f.type === "checkbox" ? (
            <label key={f.k} className="abk__check"><input type="checkbox" checked={!!form[f.k]} onChange={(e) => setForm((x) => ({ ...x, [f.k]: e.target.checked }))} /> {f.label}</label>
          ) : (
            <input key={f.k} type={f.type || "text"} className={`abk__input ${f.grow ? "abk__grow" : "abk__input--sm"}`} placeholder={f.label}
              value={form[f.k]} onChange={(e) => setForm((x) => ({ ...x, [f.k]: e.target.value }))} required={f.required} />
          ))}
          <button type="submit" className="a-btn a-btn--rust" disabled={busy}>{editId ? "Save" : <><HiOutlinePlus /> Add</>}</button>
          {editId && <button type="button" className="a-btn a-btn--ghost" onClick={() => { setEditId(null); setForm(blank); }}>Cancel</button>}
        </form>
      )}
    </div>
  );
}

function Catalog({ canEdit, onChanged }) {
  const [acts, setActs] = useState([]);
  const [diets, setDiets] = useState([]);
  const load = useCallback(() => {
    bookingsAPI.catalog.activities().then((r) => setActs(r?.data || [])).catch(() => {});
    bookingsAPI.catalog.diets().then((r) => setDiets(r?.data || [])).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const wrap = (fn) => async (...args) => {
    try { const r = await fn(...args); toast.success(r?.message || "Saved."); load(); onChanged(); }
    catch (err) { toast.error(err.message); throw err; }
  };

  return (
    <div className="abk__catalog">
      <CatalogList
        title="Activities" Icon={HiOutlineSun} canEdit={canEdit}
        hint="The list every retreat picks its included activities from. Switching one off hides it from new bookings."
        items={acts}
        fields={[
          { k: "name", label: "Activity name", required: true, grow: true },
          { k: "description", label: "Short description", grow: true },
          { k: "default_capacity", label: "Default places", type: "number", def: 10 },
          { k: "active", label: "Active", type: "checkbox", def: true },
        ]}
        render={(a) => <><b>{a.name}</b><small>{a.description || "—"} · default {a.default_capacity} places{a.used_in ? ` · used at ${a.used_in} retreat${a.used_in !== 1 ? "s" : ""}` : ""}</small></>}
        onSave={wrap((form, id) => bookingsAPI.catalog.saveActivity(form, id))}
        onRemove={(a) => window.confirm(`Remove ${a.name}?`) && wrap(() => bookingsAPI.catalog.removeActivity(a.id))().catch(() => {})}
      />
      <CatalogList
        title="Diet options" Icon={HiOutlineHeart} canEdit={canEdit}
        hint="Members pick one when booking, plus free-text notes for allergies. Shows up on the kitchen list."
        items={diets}
        fields={[
          { k: "name", label: "Diet name", required: true, grow: true },
          { k: "sort_order", label: "Order", type: "number", def: 0 },
          { k: "active", label: "Active", type: "checkbox", def: true },
        ]}
        render={(d) => <b>{d.name}</b>}
        onSave={wrap((form, id) => bookingsAPI.catalog.saveDiet(form, id))}
        onRemove={(d) => window.confirm(`Remove ${d.name}?`) && wrap(() => bookingsAPI.catalog.removeDiet(d.id))().catch(() => {})}
      />
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   PAGE
════════════════════════════════════════════════════════ */
const TABS = [
  { k: "overview", label: "Overview", Icon: HiOutlineSquares2X2 },
  { k: "bookings", label: "Bookings", Icon: HiOutlineQueueList },
  { k: "setup", label: "Setup", Icon: HiOutlineCog6Tooth },
  { k: "catalog", label: "Catalog", Icon: HiOutlineSun },
];

export default function AdminBookings() {
  const { admin, loading: permLoading } = useCurrentAdmin();
  /* Makua sells through reservations (V2.1): this screen is its read-only
     archive of the earlier bookings. Setting up dates, add-ons and diets
     moved to Retreat Schedule, Activities & Add-ons and Kitchen & Dining. */
  const archive = !!admin?.features?.reservations;
  const canEdit = permLoading || archive ? false : hasPermission(admin, "blocks.edit");
  const canManage = permLoading || archive ? false : hasPermission(admin, "queue.manage");
  const tabs = archive ? TABS.filter((t) => t.k === "overview" || t.k === "bookings") : TABS;

  const [blocks, setBlocks] = useState([]);
  const [blockId, setBlockId] = useState(null);
  const [data, setData] = useState(null);
  const [diets, setDiets] = useState([]);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (permLoading) return;
    retreatBlocksAPI.list({ scope: archive ? "all" : "active", ...(archive ? { archived: "include" } : {}) })
      .then((r) => {
        const list = (r?.data || []).filter((b) => b.status !== "cancelled");
        setBlocks(list);
        if (list.length) setBlockId(list[0].id);
        else setLoading(false);
      })
      .catch(() => setLoading(false));
    bookingsAPI.catalog.diets().then((r) => setDiets(r?.data || [])).catch(() => {});
  }, [permLoading, archive]);

  const load = useCallback(() => {
    if (!blockId) return;
    setLoading(true);
    bookingsAPI.overview(blockId)
      .then((r) => setData(r?.data || null))
      .catch((err) => { toast.error(err.message || "Could not load bookings."); setData(null); })
      .finally(() => setLoading(false));
  }, [blockId]);
  useEffect(load, [load]);

  const block = data?.block;

  return (
    <AdminLayout pageTitle={archive ? "Earlier Bookings" : "Bookings"}
      pageSubtitle={archive ? "The archive of bookings made before reservations. Read-only: nothing here was changed or removed." : "Pathways, rooms, activities and diets — who's coming and what they chose."} permission="blocks.view">
      {archive && (
        <p className="acard" style={{ padding: "0.8rem 1rem", margin: "0 0 1rem", fontSize: "0.86rem", color: "var(--admin-muted)" }}>
          New bookings are in <Link to="/admin/reservations">Reservations</Link>. Dates and their add-ons are set up in <Link to="/admin/retreats">Retreat Schedule</Link>,
          the add-on library is <Link to="/admin/activities">Activities &amp; Add-ons</Link>, and the diet list is in <Link to="/admin/kitchen?tab=diets">Kitchen &amp; Dining</Link>.
        </p>
      )}
      {blocks.length === 0 && !loading ? (
        <div className="acard abk__empty">
          <HiOutlineCalendarDays />
          <p>{archive ? "There are no earlier bookings to show." : <>No active retreats yet. Create one in <Link to="/admin/retreat-blocks">Retreat Blocks</Link>, then set up its pathways here.</>}</p>
        </div>
      ) : (
        <>
          <div className="abk__topbar">
            <label className="abk__picker">
              <HiOutlineCalendarDays />
              <select aria-label="Retreat block" value={blockId || ""} onChange={(e) => { setBlockId(Number(e.target.value)); }}>
                {blocks.map((b) => <option key={b.id} value={b.id}>{b.name} · {fmtY(b.start_date)}</option>)}
              </select>
            </label>
            {block && (
              <span className="abk__block-meta">
                {fmt(block.start_date)} – {fmtY(block.end_date)} · {block.nights} nights{block.location ? ` · ${block.location}` : ""}
                <span className={`abk__chip abk__chip--${block.status}`}>{block.status.replace("_", " ")}</span>
              </span>
            )}
            <span className="abk__spacer" />
            {block && (
              <button type="button" className="a-btn a-btn--outline" onClick={() => bookingsAPI.exportCsv(block.id, block.name).catch((e) => toast.error(e.message))}>
                <HiOutlineArrowDownTray /> Export CSV
              </button>
            )}
          </div>

          <div className="abk__tabs" role="tablist">
            {tabs.map((t) => (
              <button key={t.k} type="button" role="tab" aria-selected={tab === t.k} className={`abk__tab ${tab === t.k ? "is-on" : ""}`} onClick={() => setTab(t.k)}>
                <t.Icon /> {t.label}
                {t.k === "bookings" && data && <span className="abk__tab-count">{data.totals.booked}</span>}
              </button>
            ))}
          </div>

          {loading && !data ? (
            <div className="abk__loading"><span className="abk__spinner" /></div>
          ) : !data ? null : (
            <div className={loading ? "abk__dim" : ""}>
              {tab === "overview" && <Overview data={data} />}
              {tab === "bookings" && <BookingsTable data={data} onOpen={setOpen} />}
              {tab === "setup" && <Setup data={data} blocks={blocks} canEdit={canEdit} onChanged={load} />}
              {tab === "catalog" && <Catalog canEdit={canEdit} onChanged={() => { load(); bookingsAPI.catalog.diets().then((r) => setDiets(r?.data || [])).catch(() => {}); }} />}
            </div>
          )}

          {open && data && (
            <BookingModal booking={open} data={data} dietsCatalog={diets} canManage={canManage}
              onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />
          )}
        </>
      )}
    </AdminLayout>
  );
}
